import { useState, type FormEvent, type ReactNode } from "react";
import { CompleteDialog } from "./CompleteDialog";
import { useCampaign } from "./context";
import { CampaignError, useAction } from "./parts";
import { CloseSessionDialog, dayOf, deadlineText, fieldOf, NoteDialog, useChoiceLabel } from "./play";
import type { CampaignDay, CampaignEntry } from "./types";

/** Fields only play sets; the entry page offers the actions for them instead of a free input (status, completion, material). */
export const playFields: Record<string, string[]> = {
  adventure: ["status", "completedOn"],
  session: ["status"],
  scene: ["material"],
  thread: ["status", "deadline"],
};

/** The actions of play on the page of an adventure, session, scene or thread. */
export function EntryPlay({ entry }: { entry: CampaignEntry }) {
  const campaign = useCampaign();
  if (!campaign.canWrite) return null;
  switch (entry.kind) {
    case "adventure":
      return <AdventurePlay entry={entry} />;
    case "session":
      return <SessionPlay entry={entry} />;
    case "scene":
      return <ScenePlay entry={entry} />;
    case "thread":
      return <ThreadPlay id={entry.id} status={fieldOf(entry, "status") as string | null} deadline={dayOf(entry, "deadline")} />;
    default:
      return null;
  }
}

function PlayCard({ children, status }: { children: ReactNode; status: string }) {
  return (
    <section className="card play-panel" aria-label="Spielablauf">
      <div className="row">
        <span className="badge">{status}</span>
        <div className="actions grow">{children}</div>
      </div>
    </section>
  );
}

/** Status and completion of an adventure, on its page and in the list of adventures. */
export function AdventurePlay({ entry }: { entry: Pick<CampaignEntry, "id" | "title" | "visibility" | "fields"> }) {
  const campaign = useCampaign();
  const label = useChoiceLabel();
  const action = useAction();
  const [completing, setCompleting] = useState(false);
  const status = (fieldOf(entry, "status") as string | null) ?? "planned";
  const setStatus = (value: string) => action.run(() => campaign.apply({ type: "campaign.adventure.status", payload: { id: entry.id, status: value } }));
  const completedOn = dayOf(entry, "completedOn");
  return (
    <PlayCard status={label("adventure", "status", status)}>
      {status === "planned" && (
        <button type="button" className="button secondary" disabled={action.busy} onClick={() => void setStatus("running")}>
          Beginnen
        </button>
      )}
      {status === "running" && (
        <button type="button" className="button secondary" disabled={action.busy} onClick={() => void setStatus("planned")}>
          Zurück auf geplant
        </button>
      )}
      {status !== "completed" ? (
        <button type="button" className="button primary" onClick={() => setCompleting(true)}>
          Abschließen
        </button>
      ) : (
        <span className="muted small">
          {completedOn && <>Abgeschlossen: {completedOn.date.de}. </>}Zurücknehmen geht mit Rückgängig.
        </span>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
      {completing && <CompleteDialog adventure={entry} onClose={() => setCompleting(false)} />}
    </PlayCard>
  );
}

function SessionPlay({ entry }: { entry: CampaignEntry }) {
  const campaign = useCampaign();
  const label = useChoiceLabel();
  const action = useAction();
  const [dialog, setDialog] = useState<"note" | "close" | null>(null);
  const status = fieldOf(entry, "status") as string | null;
  const running = status === "running";
  return (
    <PlayCard status={status ? label("session", "status", status) : "vorbereitet"}>
      {running && (
        <>
          <button type="button" className="button secondary" onClick={() => setDialog("note")}>
            Notiz
          </button>
          <button type="button" className="button primary" onClick={() => setDialog("close")}>
            Abschließen
          </button>
        </>
      )}
      {!status && (
        <button
          type="button"
          className="button primary"
          disabled={action.busy}
          onClick={() => void action.run(() => campaign.apply({ type: "campaign.session.begin", payload: { id: entry.id } }))}
        >
          Beginnen
        </button>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
      {dialog === "note" && <NoteDialog onClose={() => setDialog(null)} />}
      {dialog === "close" && <CloseSessionDialog sessionId={entry.id} title={entry.title} onClose={() => setDialog(null)} />}
    </PlayCard>
  );
}

function ScenePlay({ entry }: { entry: CampaignEntry }) {
  const campaign = useCampaign();
  const action = useAction();
  const active = campaign.head.play.sceneId === entry.id;
  return (
    <PlayCard status={active ? "aktiv" : "nicht aktiv"}>
      {active ? (
        <button type="button" className="button secondary" disabled={action.busy} onClick={() => void action.run(() => campaign.apply({ type: "campaign.scene.end", payload: {} }))}>
          Beenden
        </button>
      ) : (
        <button
          type="button"
          className="button primary"
          disabled={action.busy}
          onClick={() => void action.run(() => campaign.apply({ type: "campaign.scene.begin", payload: { id: entry.id } }))}
        >
          Beginnen
        </button>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
    </PlayCard>
  );
}

/** Status and deadline of a thread; the deadline is a game day, shown with its date and its distance to today's from the API. */
export function ThreadPlay(props: { id: string; status: string | null; deadline: CampaignDay | null }) {
  const campaign = useCampaign();
  const label = useChoiceLabel();
  const action = useAction();
  const { id, deadline } = props;
  const status = props.status ?? "open";
  const [day, setDay] = useState(deadline ? String(deadline.number) : "");
  const choices = campaign.kind("thread")?.fields.find((f) => f.key === "status")?.choices ?? [];
  const saveDeadline = (e: FormEvent) => {
    e.preventDefault();
    const value = day.trim() === "" ? null : Number(day);
    if (value != null && (!Number.isInteger(value) || value < 1)) return;
    if (value !== (deadline?.number ?? null)) void action.run(() => campaign.apply({ type: "campaign.thread.status", payload: { id, deadline: value } }));
  };
  return (
    <section className="card play-panel" aria-label={`${campaign.kindLabel("thread")}: Status und Frist`}>
      <div className="inline-form two">
        <label className="field">
          <span>Status</span>
          <select
            value={status}
            onChange={(e) => void action.run(() => campaign.apply({ type: "campaign.thread.status", payload: { id, status: e.target.value } }))}
          >
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label.de}
              </option>
            ))}
          </select>
        </label>
        <form className="row" onSubmit={saveDeadline}>
          <label className="field grow">
            <span>Frist ({campaign.term("day")})</span>
            <input type="number" min={1} step={1} value={day} onChange={(e) => setDay(e.target.value)} />
          </label>
          <button type="submit" className="button secondary" disabled={action.busy}>
            Frist setzen
          </button>
        </form>
      </div>
      {deadline && (
        <p className="muted small">
          Frist {deadlineText(deadline.offset)}: {deadline.date.de} ({label("thread", "status", status)})
        </p>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
    </section>
  );
}
